import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Flex, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../PreviewHeader.module.css';
import CustomShipmentText from '../../CustomShipmentText';
import {
  useRoleLiteDropdown,
  useTeamLiteDropdown,
  useSessionTransition,
} from '@/hooks';
import { RolePermissionPreviewApi } from '@/apis/roles';
import { useSpin } from '@/components/SpinContext';
import { ApiErrorResponse } from '../PreviewHeader.types';
import PreviewDropdown, {
  PreviewDropdownOptions,
} from '../PreviewDropdown/PreviewDropdown';
import {
  setPreviewRole,
  setPreviewTeams,
  setShouldShowRolePreview,
} from '@/redux-toolkit/slice/previewSlice';
import type { RootState } from '@/redux-toolkit/store';
import { useToken } from '@/common/helper';

export const PreviewRoleBanner: React.FC = () => {
  const dispatch = useDispatch();
  const { showSpin, hideSpin } = useSpin();
  const { applyAuthSession } = useSessionTransition();
  const { getPreviewState } = useToken();
  const { isPreview } = getPreviewState();

  const shouldShowRolePreview = useSelector(
    (state: RootState) => state.preview.shouldShowRolePreview
  );

  // Auto-start ONLY when explicitly triggered AND not already in an active preview session
  const shouldAutoStart = shouldShowRolePreview && !isPreview;
  const hasAutoStarted = useRef(false);

  // Clean up any lingering trigger flag once in preview mode
  useEffect(() => {
    if (isPreview && shouldShowRolePreview) {
      dispatch(setShouldShowRolePreview(false));
    }
  }, [isPreview, shouldShowRolePreview, dispatch]);

  const previewRole = useSelector(
    (state: RootState) => state.preview.previewRole
  );
  const previewTeams = useSelector(
    (state: RootState) => state.preview.previewTeams
  );

  const [selectedRole, setSelectedRole] = useState<PreviewDropdownOptions[]>(
    () => (previewRole ? [{ id: previewRole.id, name: previewRole.name }] : [])
  );
  const [selectedTeams, setSelectedTeams] = useState<PreviewDropdownOptions[]>(
    () =>
      previewTeams && previewTeams.length > 0
        ? previewTeams.map((t) => ({ id: t.id, name: t.name }))
        : []
  );

  // Sync state if Redux changes (e.g. from page reload / hydration)
  useEffect(() => {
    if (previewRole && selectedRole.length === 0) {
      setSelectedRole([{ id: previewRole.id, name: previewRole.name }]);
    }
  }, [previewRole, selectedRole.length]);

  useEffect(() => {
    if (previewTeams && previewTeams.length > 0 && selectedTeams.length === 0) {
      setSelectedTeams(previewTeams.map((t) => ({ id: t.id, name: t.name })));
    }
  }, [previewTeams, selectedTeams.length]);

  // ─── Role dropdown  (GET /api/auth/roles/?lite=true) ────────────────────────
  const {
    roleList,
    loading: isRolesLoading,
    search: roleSearch,
    setSearch: setRolesSearch,
    handlePopupScroll: handleRolesScroll,
  } = useRoleLiteDropdown();

  // ─── Team dropdown  (GET /api/auth/teams/?lite=true) ────────────────────────
  const {
    teamList,
    loading: isTeamsLoading,
    search: teamsSearch,
    setSearch: setTeamsSearch,
    handlePopupScroll: handleTeamsScroll,
  } = useTeamLiteDropdown();

  const triggerRolePreview = useCallback(
    async (
      roleId: string,
      teamIds: string[],
      roleItem?: PreviewDropdownOptions,
      teamItems?: PreviewDropdownOptions[],
      showSuccessMsg?: boolean,
      showSpinner?: boolean,
      preserveRoute?: boolean
    ) => {
      if (!roleId || teamIds.length === 0) return;
      if (showSpinner) {
        showSpin();
      }

      try {
        const res = await RolePermissionPreviewApi({
          role_id: roleId,
          team_ids: teamIds,
        });

        const resData = res?.data?.data;
        const isSuccess = resData?.is_success;
        const access = resData?.data?.access;
        const refresh = resData?.data?.refresh;

        if (isSuccess && access) {
          const successMsg =
            resData?.message || 'Role preview updated successfully';
          const isUpdated = await applyAuthSession({
            access,
            refresh,
            successMessage: showSuccessMsg === true ? successMsg : '',
            mode: 'enterPreview',
            preserveRoute,
          });

          if (isUpdated) {
            if (roleItem) {
              dispatch(
                setPreviewRole({ id: roleItem.id, name: roleItem.name })
              );
            }
            if (teamItems && teamItems.length > 0) {
              dispatch(
                setPreviewTeams(
                  teamItems.map((t) => ({ id: t.id, name: t.name }))
                )
              );
            }
            dispatch(setShouldShowRolePreview(false));
          }
        } else {
          const errorMsg = resData?.message || 'Failed to update role preview';
          message.error(errorMsg);
          dispatch(setShouldShowRolePreview(false));
        }
      } catch (error: unknown) {
        const err = error as ApiErrorResponse & {
          message?: string;
          data?: { detail?: string; message?: string };
        };
        const errorMsg =
          err?.data?.message ||
          err?.data?.detail ||
          err?.message ||
          'Failed to update role preview';
        message.error(errorMsg);
        dispatch(setShouldShowRolePreview(false));
      } finally {
        hideSpin();
      }
    },
    [showSpin, hideSpin, applyAuthSession, dispatch]
  );

  // ─── Auto-bootstrap: select Member role + Default team on initial mount ────────
  useEffect(() => {
    if (
      shouldAutoStart &&
      !hasAutoStarted.current &&
      !isRolesLoading &&
      !isTeamsLoading
    ) {
      // Pick 'Member' role if found, else index 0, else none
      const targetRole =
        roleList?.find((r) => r.name?.trim().toLowerCase() === 'member') ??
        (roleList?.length > 0 ? roleList[0] : undefined);

      // Pick 'Default' team if found, else index 0, else none
      const targetTeam =
        teamList?.find(
          (t) =>
            t.name?.trim().toLowerCase() === 'default' ||
            Boolean((t as { is_default?: boolean }).is_default)
        ) ?? (teamList.length > 0 ? teamList[0] : undefined);

      if (targetRole && targetTeam) {
        hasAutoStarted.current = true;
        setSelectedRole([targetRole]);
        setSelectedTeams([targetTeam]);
        triggerRolePreview(
          targetRole.id,
          [targetTeam.id],
          targetRole,
          [targetTeam],
          true,
          true
        );
      } else {
        setSelectedRole([]);
        setSelectedTeams([]);
      }
    }
  }, [
    shouldAutoStart,
    roleList,
    teamList,
    isRolesLoading,
    isTeamsLoading,
    triggerRolePreview,
  ]);

  const handleRoleChange = (role: PreviewDropdownOptions[]) => {
    setSelectedRole(role);
    const newRoleId = role[0]?.id;
    if (newRoleId && selectedTeams.length > 0) {
      const teamIds = selectedTeams.map((t) => t.id).filter(Boolean);
      if (teamIds.length > 0) {
        triggerRolePreview(
          newRoleId,
          teamIds,
          role[0],
          selectedTeams,
          false,
          false,
          true
        );
      }
    }
  };

  const handleTeamsChange = (teams: PreviewDropdownOptions[]) => {
    setSelectedTeams(teams);
    const activeRoleId = selectedRole[0]?.id;
    if (activeRoleId && teams.length > 0) {
      const teamIds = teams.map((t) => t.id).filter(Boolean);
      if (teamIds.length > 0) {
        triggerRolePreview(
          activeRoleId,
          teamIds,
          selectedRole[0],
          teams,
          false,
          false,
          true
        );
      }
    }
  };

  return (
    <div className={styles.bannerContent} data-testid='role-singleselect'>
      <Flex align='center' gap={4}>
        <CustomShipmentText
          text={'Previewing role:'}
          lineHeight='20px'
          color={'#fff'}
        />

        {/* Role Selector – single select, infinite scroll */}
        <div data-testid='role-selector'>
          <PreviewDropdown
            options={roleList}
            selectedOptions={selectedRole}
            onChange={handleRoleChange}
            onSearch={setRolesSearch}
            optionsSearch={roleSearch}
            onPopupScroll={handleRolesScroll}
            loading={isRolesLoading}
            showSearch={
              roleSearch || (roleList && roleList.length > 10) ? true : false
            }
            className={`${styles.previewHeaderDd} ${styles.roleDropdown}`}
            singleSelect={true}
            showClearIcon={false}
          />
        </div>

        {/* Colon Separator */}
        <span className={styles.roleSeparator}>:</span>

        {/* Team multi-select – infinite scroll */}
        <div data-testid='team-multiselect'>
          <PreviewDropdown
            options={teamList}
            selectedOptions={selectedTeams}
            onChange={handleTeamsChange}
            onSearch={setTeamsSearch}
            onPopupScroll={handleTeamsScroll}
            loading={isTeamsLoading}
            optionsSearch={teamsSearch}
            className={`${styles.previewHeaderDd} ${styles.teamDropdown}`}
            showSearch={
              teamsSearch || (teamList && teamList.length > 10) ? true : false
            }
            showClearIcon={false}
          />
        </div>
      </Flex>
    </div>
  );
};

export default PreviewRoleBanner;
